// ABOUTME: Protected route wrapper that requires authentication.
// ABOUTME: Redirects unauthenticated users to login flow.

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, login, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Store the attempted location for redirect after login
    const returnTo = location.pathname + location.search;

    // In development with mock auth, auto-login
    if (import.meta.env.VITE_USE_MOCK_AUTH === 'true' || !import.meta.env.VITE_AUTH0_DOMAIN) {
      login();
      return (
        <div className="flex items-center justify-center h-screen bg-surface-50">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    // Redirect to login
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  // Email verification gate — redirect unverified users to verification page
  if (user && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
}
